/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../modals/Modal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { Pressable, Text, TouchableOpacity, View } from 'react-native';
import { colors, spacing } from '../theme/public-api';
import {
  CATALOG_FILTERS_COLLAPSED_STORAGE_KEY,
  POS_FILTERS_COLLAPSED_STORAGE_KEY,
  STOCK_FILTERS_COLLAPSED_STORAGE_KEY,
} from './operationalFiltersCollapsedStorage';
import { MasterFilterCompactPicker } from './MasterFilterCompactPicker';
import { MasterFilterSheetModal, type MasterFilterOption } from './MasterFilterSheetModal';
import { OperationalFiltersCard } from './OperationalFiltersCard';
import {
  OperationalFilterAvailabilityRow,
  OperationalFilterChipGroup,
  OperationalFilterChipScrollRow,
  OperationalFilterFieldLabel,
  OperationalFilterPickerRow,
  OperationalFilterSectionDivider,
  OperationalFilterSegmentedRow,
} from './OperationalFilterPrimitives';
import { OperationalFilterToggleChip } from './OperationalFilterToggleChip';
import { operationalFilterStyles as styles } from './operationalFilters.styles';
import { useOperationalFilterLayout } from './useOperationalFilterLayout';

type CategoryAppliesTo = 'product' | 'service' | 'both';

export type CatalogFilter = 'active' | 'inactive' | 'all';
export type CatalogItemTypeFilter = 'all' | 'physical' | 'service' | 'package';

export type CategoryFilterOption = {
  id: string | 'all';
  label: string;
  appliesTo?: CategoryAppliesTo;
};

const COLLAPSED_STORAGE_BY_MODE: Record<PosCatalogStockFiltersMode, string> = {
  pos: POS_FILTERS_COLLAPSED_STORAGE_KEY,
  catalog: CATALOG_FILTERS_COLLAPSED_STORAGE_KEY,
  stock: STOCK_FILTERS_COLLAPSED_STORAGE_KEY,
};

const ITEM_TYPE_OPTIONS: Array<{
  value: CatalogItemTypeFilter;
  label: string;
  shortLabel?: string;
  accessibilityLabel?: string;
}> = [
  { value: 'all', label: 'Todos', accessibilityLabel: 'Mostrar productos y servicios' },
  {
    value: 'physical',
    label: 'Productos',
    shortLabel: 'Producto',
    accessibilityLabel: 'Mostrar solo productos',
  },
  {
    value: 'service',
    label: 'Servicios',
    shortLabel: 'Servicio',
    accessibilityLabel: 'Mostrar servicios',
  },
];

const PACKAGE_ITEM_TYPE_OPTION = {
  value: 'package' as const,
  label: 'Paquetes',
  shortLabel: 'Paquete',
  accessibilityLabel: 'Mostrar paquetes de servicios',
};

export type PosCatalogStockFiltersMode = 'pos' | 'catalog' | 'stock';

type Props = {
  mode: PosCatalogStockFiltersMode;
  panelTitle?: string;
  /** Fila meta (resultados + borrar filtros) — Catálogo */
  metaRow?: React.ReactNode;
  /** 'sheet': sin card wrapper, para usar dentro de FilterSheetModal */
  sheetMode?: boolean;
  itemTypeFilter?: CatalogItemTypeFilter;
  onItemTypeFilterChange?: (value: CatalogItemTypeFilter) => void;
  /** POS: suma la opción "Paquetes" al filtro de tipo (capability `service_packages`). */
  showPackagesOption?: boolean;
  statusFilters?: Array<{ id: CatalogFilter; label: string }>;
  statusFilter?: CatalogFilter;
  onStatusFilterChange?: (filter: CatalogFilter) => void;
  categoryLabel: string;
  categoryActive?: boolean;
  categoryFilters?: CategoryFilterOption[];
  categoryFilterId?: string | null | 'all';
  onCategoryFilterChange?: (id: string | null | 'all') => void;
  onOpenCategoryPicker?: () => void;
  sortOptions?: Array<{ id: string; label: string }>;
  sortBy?: string;
  onSortChange?: (sort: string) => void;
  defaultSort?: string;
  brandFilter?: string | null;
  onBrandFilterChange?: (id: string | null) => void;
  brandOptions?: Array<{ id: string; name: string }>;
  supplierFilter?: string | null;
  onSupplierFilterChange?: (id: string | null) => void;
  supplierOptions?: Array<{ id: string; name: string }>;
  physicalStockOnly?: boolean;
  stockOnlyFilterDisabled?: boolean;
  onTogglePhysicalStockOnly?: () => void;
  stockOnlyHint?: string;
  /** Panel colapsable: estado persistido en AsyncStorage. */
  collapsible?: boolean;
  panelActiveFilterCount?: number;
  /** Clave AsyncStorage para colapsado (default según `mode`). */
  collapsedStorageKey?: string;
  alertFilter?: boolean;
  onAlertFilterChange?: (value: boolean) => void;
  alertsOnPage?: number;
  incompleteFilter?: boolean;
  onIncompleteFilterChange?: (value: boolean) => void;
  incompleteOnPage?: number;
  stockStatusFilters?: Array<{ id: string; label: string }>;
  stockStatusFilter?: string;
  onStockStatusFilterChange?: (value: string) => void;
  /** POS: oculta «Solo con stock» cuando la org apagó capability `inventory`. */
  showStockAvailabilityFilters?: boolean;
};

export function PosCatalogStockFiltersPanel({
  mode,
  panelTitle,
  metaRow,
  sheetMode = false,
  itemTypeFilter = 'all',
  onItemTypeFilterChange,
  showPackagesOption = false,
  statusFilters,
  statusFilter,
  onStatusFilterChange,
  categoryLabel,
  categoryActive = false,
  categoryFilters = [],
  categoryFilterId = 'all',
  onCategoryFilterChange,
  onOpenCategoryPicker,
  sortOptions = [],
  sortBy,
  onSortChange,
  defaultSort = 'updated_desc',
  brandFilter = null,
  onBrandFilterChange,
  brandOptions = [],
  supplierFilter = null,
  onSupplierFilterChange,
  supplierOptions = [],
  physicalStockOnly = false,
  stockOnlyFilterDisabled = false,
  onTogglePhysicalStockOnly,
  stockOnlyHint,
  collapsible = false,
  panelActiveFilterCount = 0,
  collapsedStorageKey,
  alertFilter = false,
  onAlertFilterChange,
  alertsOnPage = 0,
  incompleteFilter = false,
  onIncompleteFilterChange,
  incompleteOnPage = 0,
  stockStatusFilters,
  stockStatusFilter,
  onStockStatusFilterChange,
  showStockAvailabilityFilters = true,
}: Props) {
  const { variant, layoutWide } = useOperationalFilterLayout();
  const resolvedCollapsedStorageKey =
    collapsedStorageKey ?? COLLAPSED_STORAGE_BY_MODE[mode] ?? POS_FILTERS_COLLAPSED_STORAGE_KEY;
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [brandSheetOpen, setBrandSheetOpen] = useState(false);
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [stockStatusSheetOpen, setStockStatusSheetOpen] = useState(false);
  const [moreFiltersSheetOpen, setMoreFiltersSheetOpen] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  /** Ticket stack inside FilterSheetModal (POS / Stock / Catalog). */
  const isSheetClean = sheetMode;
  const brandEmptyLabel = isSheetClean ? 'Cualquiera' : 'Todas';
  const supplierEmptyLabel = isSheetClean ? 'Cualquiera' : 'Todos';

  /** Vista Paquetes (POS): la grilla lista service_packages — no aplican filtros de catálogo. */
  const packageMode = itemTypeFilter === 'package';

  const itemTypeOptions = useMemo(
    () => (showPackagesOption ? [...ITEM_TYPE_OPTIONS, PACKAGE_ITEM_TYPE_OPTION] : ITEM_TYPE_OPTIONS),
    [showPackagesOption]
  );

  const showItemType = mode !== 'stock' && !!onItemTypeFilterChange;
  const showStatus = mode === 'catalog' && !!statusFilters && !!onStatusFilterChange;
  const showStockToggle =
    showStockAvailabilityFilters && mode === 'pos' && !!onTogglePhysicalStockOnly && !packageMode;
  const showStockStatus = mode === 'stock' && !!stockStatusFilters && !!onStockStatusFilterChange;
  const showSort =
    (mode === 'catalog' || mode === 'stock') &&
    sortOptions.length > 0 &&
    !!onSortChange &&
    sortBy != null &&
    !packageMode;

  const brandLabel = useMemo(() => {
    if (!brandFilter) return brandEmptyLabel;
    return brandOptions.find((b) => b.id === brandFilter)?.name ?? brandEmptyLabel;
  }, [brandEmptyLabel, brandFilter, brandOptions]);

  const supplierLabel = useMemo(() => {
    if (!supplierFilter) return supplierEmptyLabel;
    return supplierOptions.find((s) => s.id === supplierFilter)?.name ?? supplierEmptyLabel;
  }, [supplierEmptyLabel, supplierFilter, supplierOptions]);

  const sortLabel = useMemo(
    () => sortOptions.find((s) => s.id === sortBy)?.label ?? 'Orden',
    [sortBy, sortOptions]
  );

  const showBrandSupplier =
    !!onBrandFilterChange &&
    !!onSupplierFilterChange &&
    (brandOptions.length > 0 || supplierOptions.length > 0) &&
    !packageMode;

  useEffect(() => {
    if (!collapsible) return;
    let alive = true;
    void AsyncStorage.getItem(resolvedCollapsedStorageKey).then((raw) => {
      if (!alive) return;
      setFiltersCollapsed(raw === '1');
    });
    return () => {
      alive = false;
    };
  }, [collapsible, resolvedCollapsedStorageKey]);

  const setFiltersCollapsedPersisted = (next: boolean) => {
    setFiltersCollapsed(next);
    void AsyncStorage.setItem(resolvedCollapsedStorageKey, next ? '1' : '0');
  };

  const toggleFiltersCollapsed = () => {
    setFiltersCollapsedPersisted(!filtersCollapsed);
  };

  const moreFiltersSummary = useMemo(() => {
    const parts: string[] = [];
    if (brandFilter) {
      parts.push(brandOptions.find((b) => b.id === brandFilter)?.name ?? 'Marca');
    }
    if (supplierFilter) {
      parts.push(supplierOptions.find((s) => s.id === supplierFilter)?.name ?? 'Proveedor');
    }
    if (parts.length === 0) return 'Marca y proveedor';
    return parts.join(' · ');
  }, [brandFilter, brandOptions, supplierFilter, supplierOptions]);

  const stockStatusLabel = useMemo(() => {
    if (!stockStatusFilter || stockStatusFilter === 'all') return 'Todos';
    return stockStatusFilters?.find((f) => f.id === stockStatusFilter)?.label ?? 'Todos';
  }, [stockStatusFilter, stockStatusFilters]);

  const stockStatusSheetOptions = useMemo<MasterFilterOption[]>(
    () =>
      (stockStatusFilters ?? [])
        .filter((f) => f.id !== 'all')
        .map((f) => ({ id: f.id, name: f.label })),
    [stockStatusFilters]
  );

  const showPosMobileMoreFilters =
    mode === 'pos' && !layoutWide && !sheetMode && showBrandSupplier && (brandOptions.length > 0 || supplierOptions.length > 0);

  const categorySheetOptions = useMemo<MasterFilterOption[]>(
    () =>
      categoryFilters
        .filter((c) => c.id !== 'all')
        .map((c) => ({
          id: String(c.id),
          name: c.label,
        })),
    [categoryFilters]
  );

  const sortSheetOptions = useMemo<MasterFilterOption[]>(
    () => sortOptions.map((s) => ({ id: s.id, name: s.label })),
    [sortOptions]
  );

  const openCategory = () => {
    if (onOpenCategoryPicker) {
      onOpenCategoryPicker();
      return;
    }
    if (categorySheetOptions.length > 0) setCategorySheetOpen(true);
  };

  const itemTypeControls =
    variant === 'mobile' && !sheetMode ? (
      <OperationalFilterSegmentedRow
        options={itemTypeOptions}
        value={itemTypeFilter}
        onChange={(v) => onItemTypeFilterChange?.(v)}
      />
    ) : (
      <OperationalFilterChipGroup
        options={itemTypeOptions}
        value={itemTypeFilter}
        onChange={(v) => onItemTypeFilterChange?.(v)}
      />
    );

  const statusOptions =
    statusFilters?.map((f) => ({ value: f.id, label: f.label })) ?? [];

  const statusControls =
    statusFilters && onStatusFilterChange ? (
      variant === 'mobile' ? (
        <OperationalFilterChipScrollRow
          options={statusOptions}
          value={statusFilter ?? 'all'}
          onChange={onStatusFilterChange}
        />
      ) : (
        <OperationalFilterChipGroup
          options={statusOptions}
          value={statusFilter ?? 'all'}
          onChange={onStatusFilterChange}
        />
      )
    ) : null;

  const stockStatusOptions =
    stockStatusFilters?.map((f) => ({ value: f.id, label: f.label })) ?? [];

  const stockStatusControls =
    stockStatusFilters && onStockStatusFilterChange ? (
      variant === 'mobile' ? (
        <OperationalFilterChipScrollRow
          options={stockStatusOptions}
          value={stockStatusFilter ?? 'all'}
          onChange={onStockStatusFilterChange}
        />
      ) : (
        <OperationalFilterChipGroup
          options={stockStatusOptions}
          value={stockStatusFilter ?? 'all'}
          onChange={onStockStatusFilterChange}
        />
      )
    ) : null;

  const catalogWideTypeStatusLanes =
    mode === 'catalog' && layoutWide && (showItemType || showStatus) ? (
      <View style={styles.filterLanesRow}>
        {showItemType ? (
          <View style={styles.wideToolbarGroupNarrow}>
            <OperationalFilterFieldLabel>Tipo</OperationalFilterFieldLabel>
            {itemTypeControls}
          </View>
        ) : null}
        {showStatus ? (
          <View style={styles.wideToolbarGroupNarrow}>
            <OperationalFilterFieldLabel>Estado</OperationalFilterFieldLabel>
            {statusControls}
          </View>
        ) : null}
      </View>
    ) : null;

  const stockWideStatusRow =
    mode === 'stock' && layoutWide && showStockStatus ? (
      <View style={styles.wideToolbarRow}>
        <View style={styles.wideToolbarGroup}>
          <OperationalFilterFieldLabel>Vista rápida</OperationalFilterFieldLabel>
          {stockStatusControls}
        </View>
      </View>
    ) : null;

  const posWideTypeStockRow =
    mode === 'pos' && layoutWide && (showItemType || showStockToggle) ? (
      <View style={styles.wideToolbarRow}>
        {showItemType ? (
          <View style={styles.wideToolbarGroupNarrow}>
            <OperationalFilterFieldLabel>Tipo</OperationalFilterFieldLabel>
            {itemTypeControls}
          </View>
        ) : null}
        {showStockToggle ? (
          <View style={styles.wideToolbarGroupNarrow}>
            <OperationalFilterFieldLabel>Disponibilidad</OperationalFilterFieldLabel>
            <OperationalFilterToggleChip
              label="Solo con stock"
              selected={physicalStockOnly}
              disabled={stockOnlyFilterDisabled}
              onPress={() => onTogglePhysicalStockOnly?.()}
              iconName="cube-outline"
              accessibilityLabel="Solo productos con stock en esta sucursal"
              hint={stockOnlyFilterDisabled ? stockOnlyHint : undefined}
            />
          </View>
        ) : null}
      </View>
    ) : null;

  const masterPickersWide = layoutWide ? (
    <View style={styles.wideToolbarRow}>
      <View style={styles.wideToolbarGroup}>
        <MasterFilterCompactPicker
          caption="Categoría"
          value={categoryLabel}
          active={categoryActive}
          onPress={openCategory}
        />
      </View>
      {showSort ? (
        <View style={styles.wideToolbarGroup}>
          <MasterFilterCompactPicker
            caption="Orden"
            value={sortLabel}
            active={sortBy !== defaultSort}
            onPress={() => setSortSheetOpen(true)}
          />
        </View>
      ) : null}
      {showBrandSupplier && brandOptions.length > 0 ? (
        <View style={styles.wideToolbarGroup}>
          <MasterFilterCompactPicker
            caption="Marca"
            value={brandLabel}
            active={brandFilter != null}
            onPress={() => setBrandSheetOpen(true)}
          />
        </View>
      ) : null}
      {showBrandSupplier && supplierOptions.length > 0 ? (
        <View style={styles.wideToolbarGroup}>
          <MasterFilterCompactPicker
            caption="Proveedor"
            value={supplierLabel}
            active={supplierFilter != null}
            onPress={() => setSupplierSheetOpen(true)}
          />
        </View>
      ) : null}
    </View>
  ) : null;

  const showCategoryPickerMobile =
    (Boolean(onOpenCategoryPicker) || categorySheetOptions.length > 0) && !packageMode;

  const masterPickersMobile =
    !layoutWide &&
    !isSheetClean &&
    !showPosMobileMoreFilters &&
    (showBrandSupplier || showCategoryPickerMobile) ? (
      <View style={{ gap: 6, alignSelf: 'stretch' }}>
        {showCategoryPickerMobile ? (
          <OperationalFilterPickerRow
            label="Categoría"
            valueLabel={categoryLabel}
            active={categoryActive}
            onPress={openCategory}
          />
        ) : null}
        {showSort ? (
          <OperationalFilterPickerRow
            label="Orden"
            valueLabel={sortLabel}
            active={sortBy !== defaultSort}
            onPress={() => setSortSheetOpen(true)}
          />
        ) : null}
        {showBrandSupplier && brandOptions.length > 0 ? (
          <OperationalFilterPickerRow
            label="Marca"
            valueLabel={brandLabel}
            active={brandFilter != null}
            onPress={() => setBrandSheetOpen(true)}
          />
        ) : null}
        {showBrandSupplier && supplierOptions.length > 0 ? (
          <OperationalFilterPickerRow
            label="Proveedor"
            valueLabel={supplierLabel}
            active={supplierFilter != null}
            onPress={() => setSupplierSheetOpen(true)}
          />
        ) : null}
      </View>
    ) : null;

  const posMobileMoreFilters = showPosMobileMoreFilters ? (
    <>
      <OperationalFilterPickerRow
        label="Más filtros"
        valueLabel={moreFiltersSummary}
        active={brandFilter != null || supplierFilter != null}
        onPress={() => setMoreFiltersSheetOpen(true)}
      />
      <Modal
        visible={moreFiltersSheetOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setMoreFiltersSheetOpen(false)}
      >
        <Pressable style={styles.moreFiltersBackdrop} onPress={() => setMoreFiltersSheetOpen(false)}>
          <Pressable style={styles.moreFiltersPanel} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.moreFiltersTitle}>Más filtros</Text>
            {brandOptions.length > 0 ? (
              <OperationalFilterPickerRow
                label="Marca"
                valueLabel={brandLabel}
                active={brandFilter != null}
                onPress={() => {
                  setMoreFiltersSheetOpen(false);
                  setBrandSheetOpen(true);
                }}
              />
            ) : null}
            {supplierOptions.length > 0 ? (
              <OperationalFilterPickerRow
                label="Proveedor"
                valueLabel={supplierLabel}
                active={supplierFilter != null}
                onPress={() => {
                  setMoreFiltersSheetOpen(false);
                  setSupplierSheetOpen(true);
                }}
              />
            ) : null}
            <TouchableOpacity
              style={styles.moreFiltersDoneBtn}
              onPress={() => setMoreFiltersSheetOpen(false)}
              accessibilityRole="button"
            >
              <Text style={styles.moreFiltersDoneText}>Listo</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  ) : null;

  const resolvedTitle =
    panelTitle === undefined
      ? mode === 'pos'
        ? 'Filtros'
        : mode === 'catalog'
          ? 'Filtros'
          : 'Filtros'
      : panelTitle;

  const collapsedTitle = 'Filtros';

  const filtersBodyVisible = !collapsible || !filtersCollapsed;

  const sheetCleanBody =
    isSheetClean && filtersBodyVisible ? (
      <View style={{ gap: 18, alignSelf: 'stretch' }}>
        {showItemType ? (
          <View style={{ gap: 10 }}>
            <OperationalFilterFieldLabel>Tipo de ítem</OperationalFilterFieldLabel>
            <OperationalFilterSegmentedRow
              options={itemTypeOptions}
              value={itemTypeFilter}
              onChange={(v) => onItemTypeFilterChange?.(v)}
              ticketTone
            />
          </View>
        ) : null}

        {showStockStatus ? (
          <View style={{ gap: 10 }}>
            <OperationalFilterFieldLabel>Vista rápida</OperationalFilterFieldLabel>
            <OperationalFilterPickerRow
              layout="stack"
              label="Estado"
              valueLabel={stockStatusLabel}
              active={stockStatusFilter != null && stockStatusFilter !== 'all'}
              onPress={() => setStockStatusSheetOpen(true)}
            />
          </View>
        ) : null}

        {showStockToggle ? (
          <View style={{ gap: 10 }}>
            <OperationalFilterFieldLabel>Disponibilidad</OperationalFilterFieldLabel>
            <OperationalFilterAvailabilityRow
              selected={physicalStockOnly}
              disabled={stockOnlyFilterDisabled}
              onPress={() => onTogglePhysicalStockOnly?.()}
              hint={
                stockOnlyFilterDisabled
                  ? stockOnlyHint
                  : 'Oculta ítems sin existencias en la sucursal'
              }
            />
          </View>
        ) : null}

        {showCategoryPickerMobile || showBrandSupplier || showSort ? (
          <View style={{ gap: 8 }}>
            <OperationalFilterFieldLabel>Catálogo</OperationalFilterFieldLabel>
            {showCategoryPickerMobile ? (
              <OperationalFilterPickerRow
                layout="stack"
                label="Categoría"
                valueLabel={categoryLabel}
                active={categoryActive}
                onPress={openCategory}
              />
            ) : null}
            {showBrandSupplier && brandOptions.length > 0 ? (
              <OperationalFilterPickerRow
                layout="stack"
                label="Marca"
                valueLabel={brandLabel}
                active={brandFilter != null}
                onPress={() => setBrandSheetOpen(true)}
              />
            ) : null}
            {showBrandSupplier && supplierOptions.length > 0 ? (
              <OperationalFilterPickerRow
                layout="stack"
                label="Proveedor"
                valueLabel={supplierLabel}
                active={supplierFilter != null}
                onPress={() => setSupplierSheetOpen(true)}
              />
            ) : null}
            {showSort ? (
              <OperationalFilterPickerRow
                layout="stack"
                label="Orden"
                valueLabel={sortLabel}
                active={sortBy !== defaultSort}
                onPress={() => setSortSheetOpen(true)}
              />
            ) : null}
          </View>
        ) : null}
      </View>
    ) : null;

  const panelTitleRow = collapsible ? (
    <TouchableOpacity
      style={styles.collapseHeaderRow}
      onPress={toggleFiltersCollapsed}
      accessibilityRole="button"
      accessibilityLabel={filtersCollapsed ? 'Expandir filtros' : 'Colapsar filtros'}
      accessibilityState={{ expanded: !filtersCollapsed }}
    >
      <View style={styles.collapseHeaderMain}>
        <OperationalFilterFieldLabel>{collapsedTitle}</OperationalFilterFieldLabel>
        {filtersCollapsed && panelActiveFilterCount > 0 ? (
          <View style={styles.collapsedActiveFiltersChip}>
            <Text style={styles.collapsedActiveFiltersChipText}>
              {panelActiveFilterCount} activo{panelActiveFilterCount === 1 ? '' : 's'}
            </Text>
          </View>
        ) : null}
      </View>
      <Ionicons
        name={filtersCollapsed ? 'chevron-down' : 'chevron-up'}
        size={18}
        color={colors.textMuted}
      />
    </TouchableOpacity>
  ) : resolvedTitle != null && resolvedTitle !== '' ? (
    <OperationalFilterFieldLabel>{resolvedTitle}</OperationalFilterFieldLabel>
  ) : null;

  const filtersBody = isSheetClean
    ? sheetCleanBody
    : filtersBodyVisible ? (
    <View style={{ gap: sheetMode ? 20 : spacing.sm, alignSelf: 'stretch' }}>
      {catalogWideTypeStatusLanes ?? posWideTypeStockRow ?? stockWideStatusRow ?? (
        <>
          {showItemType ? (
            <View style={{ gap: 8 }}>
              <OperationalFilterFieldLabel>Tipo de ítem</OperationalFilterFieldLabel>
              {itemTypeControls}
            </View>
          ) : null}

          {showStatus ? (
            <View style={{ gap: 8 }}>
              <OperationalFilterFieldLabel>Estado</OperationalFilterFieldLabel>
              {statusControls}
            </View>
          ) : null}

          {showStockToggle ? (
            <View style={{ gap: 8 }}>
              <OperationalFilterFieldLabel>Disponibilidad</OperationalFilterFieldLabel>
              <View style={styles.chipsWrap}>
                <OperationalFilterToggleChip
                  label="Solo con stock"
                  selected={physicalStockOnly}
                  disabled={stockOnlyFilterDisabled}
                  onPress={() => onTogglePhysicalStockOnly?.()}
                  iconName="cube-outline"
                  accessibilityLabel="Solo productos con stock en esta sucursal"
                  hint={stockOnlyFilterDisabled ? stockOnlyHint : undefined}
                />
              </View>
            </View>
          ) : null}

          {showStockStatus ? (
            <View style={{ gap: 8 }}>
              <OperationalFilterFieldLabel>Vista rápida</OperationalFilterFieldLabel>
              {stockStatusControls}
            </View>
          ) : null}
        </>
      )}

      {masterPickersMobile}
      {posMobileMoreFilters}
      {masterPickersWide}
      {mode === 'catalog' && onAlertFilterChange ? (
        <View style={{ gap: 8 }}>
          <OperationalFilterFieldLabel>Alertas</OperationalFilterFieldLabel>
          <View style={styles.chipsWrap}>
            <OperationalFilterToggleChip
              label={alertsOnPage > 0 ? `Con alerta · ${alertsOnPage}` : 'Con alerta'}
              selected={alertFilter}
              onPress={() => onAlertFilterChange(!alertFilter)}
              iconName="warning-outline"
              accessibilityLabel="Mostrar solo productos con alerta de stock o margen negativo"
            />
          </View>
        </View>
      ) : null}
      {mode === 'catalog' && onIncompleteFilterChange ? (
        <View style={{ gap: 6 }}>
          <OperationalFilterFieldLabel>Completitud</OperationalFilterFieldLabel>
          <View style={styles.chipsWrap}>
            <OperationalFilterToggleChip
              label={incompleteOnPage > 0 ? `Incompletos · ${incompleteOnPage}` : 'Incompletos'}
              selected={incompleteFilter}
              onPress={() => onIncompleteFilterChange(!incompleteFilter)}
              iconName="alert-circle-outline"
              accessibilityLabel="Mostrar solo productos con campos faltantes (código, categoría o costo)"
            />
          </View>
        </View>
      ) : null}
    </View>
  ) : null;

  return (
    <>
      {sheetMode ? (
        filtersBody
      ) : (
        <OperationalFiltersCard>
          {panelTitleRow || metaRow ? (
            <>
              {panelTitleRow}
              {metaRow}
              {filtersBodyVisible ? <OperationalFilterSectionDivider /> : null}
            </>
          ) : null}
          {filtersBody}
        </OperationalFiltersCard>
      )}

      {categorySheetOptions.length > 0 && onCategoryFilterChange && !onOpenCategoryPicker ? (
        <MasterFilterSheetModal
          visible={categorySheetOpen}
          title="Categoría"
          options={categorySheetOptions}
          selectedId={categoryFilterId === 'all' ? null : String(categoryFilterId)}
          onSelect={(id) => {
            if (id == null) onCategoryFilterChange('all');
            else onCategoryFilterChange(id);
          }}
          onClose={() => setCategorySheetOpen(false)}
          allOptionLabel="Todas"
          appearance={isSheetClean ? 'ticket' : 'default'}
        />
      ) : null}

      {showSort ? (
        <MasterFilterSheetModal
          visible={sortSheetOpen}
          title="Orden del listado"
          options={sortSheetOptions}
          selectedId={sortBy ?? defaultSort}
          onSelect={(id) => {
            if (id) onSortChange?.(id);
          }}
          onClose={() => setSortSheetOpen(false)}
          showAllOption={false}
          appearance={isSheetClean ? 'ticket' : 'default'}
        />
      ) : null}

      {showStockStatus ? (
        <MasterFilterSheetModal
          visible={stockStatusSheetOpen}
          title="Vista rápida"
          options={stockStatusSheetOptions}
          selectedId={!stockStatusFilter || stockStatusFilter === 'all' ? null : stockStatusFilter}
          onSelect={(id) => onStockStatusFilterChange?.(id ?? 'all')}
          onClose={() => setStockStatusSheetOpen(false)}
          allOptionLabel="Todos"
          appearance={isSheetClean ? 'ticket' : 'default'}
        />
      ) : null}

      {showBrandSupplier && brandOptions.length > 0 ? (
        <MasterFilterSheetModal
          visible={brandSheetOpen}
          title="Marca"
          options={brandOptions}
          selectedId={brandFilter}
          onSelect={(id) => onBrandFilterChange?.(id)}
          onClose={() => setBrandSheetOpen(false)}
          allOptionLabel={brandEmptyLabel}
          appearance={isSheetClean ? 'ticket' : 'default'}
        />
      ) : null}

      {showBrandSupplier && supplierOptions.length > 0 ? (
        <MasterFilterSheetModal
          visible={supplierSheetOpen}
          title="Proveedor"
          options={supplierOptions}
          selectedId={supplierFilter}
          onSelect={(id) => onSupplierFilterChange?.(id)}
          onClose={() => setSupplierSheetOpen(false)}
          allOptionLabel={supplierEmptyLabel}
          appearance={isSheetClean ? 'ticket' : 'default'}
        />
      ) : null}
    </>
  );
}
